import { GroupBase, StylesConfig } from 'react-select';
import { Theme } from '@tanium/theme-data';
import {
  ClearIcon,
  ClearIconActive,
  ClearIconHover,
  ContainerBorder,
  ContainerFill,
  ControlFill,
  DisabledBorder,
  DropdownDisabledIcon,
  DropdownIcon,
  DropdownIconActive,
  DropdownIconHover,
  DropdownOpenIcon,
  GroupHeadingText,
  InputDisabledFill,
  InputFill,
  InputFocus,
  InputFocusBoxShadow,
  InputText,
  InvalidBorder,
  LoadingMessage,
  MenuBorder,
  MenuFill,
  MenuListFill,
  NoOptionsText,
  OptionDisabledText,
  OptionHoverFill,
  OptionSelectedFill,
  OptionSelectedText,
  OptionText,
  PlaceholderText,
  SingleValueText,
} from './themeDefinitions';
import { SelectSize } from './types';

interface GetStylesParams {
  theme: Theme;
  size: SelectSize;
  invalid?: boolean;
}

export const getStyles = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  theme,
  size,
  invalid,
}: GetStylesParams): StylesConfig<Option, IsMulti, Group> => {
  const sizeBasedStyles =
    size === 'sm'
      ? {
          height: theme.sizes[3] - theme.borderWidths[1] * 2,
          lineHeight: '14px',
          // TODO: Get fontSize from theme
          fontSize: '12px',
        }
      : {
          height: theme.sizes[4] - theme.borderWidths[1] * 2,
          lineHeight: '16px',
          fontSize: '14px',
        };

  return {
    option: (base, state) => ({
      ...base,
      lineHeight: sizeBasedStyles.lineHeight,
      color: OptionText(theme),
      ...(state.isDisabled && { color: OptionDisabledText(theme) }),
      ...(state.isFocused && { background: OptionHoverFill(theme) }),
      ...(state.isSelected && {
        color: OptionSelectedText(theme),
        background: OptionSelectedFill(theme),
      }),
      ...(!state.isDisabled && {
        ':active': {
          background: state.isSelected ? OptionSelectedFill(theme) : OptionHoverFill(theme),
        },
      }),
    }),
    container: (base, state) => {
      let borderColor = ContainerBorder;
      if (invalid) {
        borderColor = InvalidBorder;
      } else if (state.selectProps.isDisabled) {
        borderColor = DisabledBorder;
      } else if (state.isFocused) {
        borderColor = InputFocus;
      }

      return {
        ...base,
        background: ContainerFill(theme),
        boxShadow: state.isFocused ? InputFocusBoxShadow : 'none',
        borderRadius: theme.radii[1],
        border: `${theme.borderWidths[1]}px solid ${borderColor(theme)}`,
        // Use triple specificity to ensure that when the menu is open, the button of the container
        // has no border radius (to match the top of the menu) regardless of whether, e.g., the border
        // radius is otherwise being overridden by a ControlGroup.
        '&&&': state.selectProps.menuIsOpen && {
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        },
        transition: 'all 0s',
      };
    },
    control: (base) => ({
      ...base,
      background: ControlFill(theme),
      border: 'none',
      boxShadow: 'none',
      fontSize: sizeBasedStyles.fontSize,
      lineHeight: sizeBasedStyles.lineHeight,
      margin: 0,
      minHeight: sizeBasedStyles.height,
    }),
    valueContainer: (base, state) => ({
      ...base,
      minHeight: sizeBasedStyles.height,
      paddingTop: 0,
      paddingBottom: 0,
      borderTopLeftRadius: theme.radii[1],
      borderBottomLeftRadius: theme.radii[1],
      ...(state.isDisabled
        ? {
            background: InputDisabledFill(theme),
          }
        : {
            background: InputFill(theme),
          }),
      ...(state.hasValue &&
        state.isMulti && {
          paddingLeft: theme.space.multiple / 4,
          paddingRight: theme.space.multiple / 4,
        }),
    }),
    singleValue: (base) => ({
      ...base,
      color: SingleValueText(theme),
    }),
    input: (base) => ({
      ...base,
      color: InputText(theme),
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: MenuFill(theme),
      border: `${theme.borderWidths[1]}px solid ${MenuBorder(theme)}`,
      borderRadius: theme.radii[1],
      borderTop: '0px solid transparent',
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      boxShadow: theme.shadows.Overlay2,
      fontSize: sizeBasedStyles.fontSize,
      marginLeft: -1 * theme.borderWidths[1], // Aligns menu with container's left border
      marginTop: theme.borderWidths[1], // Allows container bottom border to show
      width: `calc(100% + ${2 * theme.borderWidths[1]}px)`, // Aligns menu with container's right border
      zIndex: theme.zIndices.popover,
    }),
    menuList: (base) => ({
      ...base,
      backgroundColor: MenuListFill(theme),
    }),
    indicatorsContainer: (base, state) => ({
      ...base,
      borderTopRightRadius: theme.radii[1],
      borderBottomRightRadius: theme.radii[1],
      ...(state.isDisabled
        ? {
            background: InputDisabledFill(theme),
            color: DropdownDisabledIcon(theme),
          }
        : {
            background: InputFill(theme),
            color: DropdownIcon(theme),
          }),
    }),
    dropdownIndicator: (_base, state) => ({
      height: sizeBasedStyles.height,
      width: theme.sizes[4],
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 0,
      ...(state.selectProps.isDisabled
        ? {
            color: OptionDisabledText(theme),
          }
        : {
            ':hover': {
              color: DropdownIconHover(theme),
            },
            ':active': {
              color: DropdownIconActive(theme),
            },
            ...(state.selectProps.menuIsOpen && {
              color: DropdownOpenIcon(theme),
            }),
          }),
    }),
    clearIndicator: ({ transition, ...base }) => ({
      ...base,
      justifyContent: 'center',
      width: theme.sizes[4],
      padding: 0,
      color: ClearIcon(theme),
      ':hover': {
        color: ClearIconHover(theme),
      },
      ':active': {
        color: ClearIconActive(theme),
      },
    }),
    placeholder: (base) => ({ ...base, color: PlaceholderText(theme) }),
    noOptionsMessage: (base) => ({ ...base, color: NoOptionsText(theme) }),
    loadingMessage: (base) => ({ ...base, color: LoadingMessage(theme) }),
    groupHeading: (base) => ({ ...base, color: GroupHeadingText(theme) }),
  };
};
